import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, AlertCircle } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import ReconciliationSummaryCards from './ReconciliationSummaryCards';
import ReconciliationFilterBar from './ReconciliationFilterBar';
import ReconciliationTable from './ReconciliationTable';
import brokerService from '../../services/brokerService';
import reconciliationService from '../../services/reconciliationService';
import type { BrokerAccount } from '../../types';
import type { ReconciliationResponse } from '../../types/reconciliation';

const ACCOUNT_KEY_SEP = '::';
const accountKey = (broker: string, clientId: string) => `${broker}${ACCOUNT_KEY_SEP}${clientId}`;
const splitAccountKey = (key: string): { broker: string; clientId: string } | null => {
  const idx = key.indexOf(ACCOUNT_KEY_SEP);
  if (idx < 0) return null;
  return { broker: key.slice(0, idx), clientId: key.slice(idx + ACCOUNT_KEY_SEP.length) };
};

const ReconciliationView: React.FC = () => {
  const [accounts, setAccounts] = React.useState<BrokerAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = React.useState(true);
  const [accountsError, setAccountsError] = React.useState<string | null>(null);

  const [selectedKey, setSelectedKey] = React.useState<string>('');
  const [data, setData] = React.useState<ReconciliationResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [searchQuery, setSearchQuery] = React.useState('');
  const [mismatchesOnly, setMismatchesOnly] = React.useState(false);

  // Load broker accounts on mount.
  React.useEffect(() => {
    let cancelled = false;
    setAccountsLoading(true);
    brokerService.getBrokerAccounts()
      .then(list => {
        if (cancelled) return;
        const enabled = list.filter(a => a.account_status === 'enabled');
        setAccounts(enabled);
        if (enabled.length > 0 && !selectedKey) {
          setSelectedKey(accountKey(enabled[0].broker_name, enabled[0].client_id));
        }
      })
      .catch(err => {
        if (cancelled) return;
        setAccountsError(err?.message || 'Failed to load broker accounts');
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReconciliation = React.useCallback(async (isRefresh = false) => {
    if (!selectedKey) return;
    const parsed = splitAccountKey(selectedKey);
    if (!parsed) return;

    if (isRefresh && data) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const resp = await reconciliationService.getReconciliation({
        broker: parsed.broker,
        clientId: parsed.clientId,
      });
      setData(resp);
    } catch (e: any) {
      setError(e?.message || 'Failed to load reconciliation');
      if (!isRefresh) setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedKey, data]);

  // Fetch when account changes.
  React.useEffect(() => {
    if (selectedKey) {
      fetchReconciliation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  // Account dropdown options.
  const accountOptions = React.useMemo(() => {
    if (!accounts.length) {
      return [{ value: '', label: 'No broker accounts available' }];
    }
    return accounts.map(a => ({
      value: accountKey(a.broker_name, a.client_id),
      label: `${a.broker_name.toUpperCase()} • ${a.client_id}`,
    }));
  }, [accounts]);

  // Filter instruments client-side.
  const filteredInstruments = React.useMemo(() => {
    const list = data?.instruments || [];
    let out = list;
    if (mismatchesOnly) out = out.filter(i => !i.match);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      out = out.filter(i =>
        i.symbol.toLowerCase().includes(q) ||
        i.exchange.toLowerCase().includes(q) ||
        i.product.toLowerCase().includes(q),
      );
    }
    return out;
  }, [data, mismatchesOnly, searchQuery]);

  // ── Empty state: no broker accounts ───────────────────────────────
  if (!accountsLoading && !accountsError && accounts.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Building2 className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <div className="text-sm text-gray-700 dark:text-gray-200 mb-2">
            You don't have any enabled broker accounts.
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Connect a broker to start reconciling positions.
          </div>
          <Link
            to="/brokers"
            className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
          >
            Go to Brokers
          </Link>
        </CardContent>
      </Card>
    );
  }

  // ── Account list error ────────────────────────────────────────────
  if (accountsError) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{accountsError}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── Initial spinner while accounts are loading ────────────────────
  if (accountsLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || { total: 0, matched: 0, mismatched: 0, sideMismatched: 0, totalBrokerPnl: 0 };

  return (
    <div className="space-y-4">
      <ReconciliationSummaryCards summary={summary} />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>
              Instruments ({filteredInstruments.length})
              {mismatchesOnly && data && (
                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                  · {data.summary.total} total
                </span>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ReconciliationFilterBar
            accountOptions={accountOptions}
            selectedAccountKey={selectedKey}
            onAccountChange={setSelectedKey}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            mismatchesOnly={mismatchesOnly}
            onMismatchesOnlyChange={setMismatchesOnly}
            fetchedAt={data?.fetchedAt || null}
            onRefresh={() => fetchReconciliation(true)}
            refreshing={refreshing}
            disabled={loading || !selectedKey}
          />

          {loading && (
            <div className="px-6 py-12 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          )}

          {!loading && error && (
            <div className="px-6 py-6 flex items-center gap-2 text-red-600 dark:text-red-400 text-sm border-b border-gray-200 dark:border-gray-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && data && (
            <ReconciliationTable instruments={filteredInstruments} />
          )}

          {!loading && !error && !data && (
            <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              Select a broker account to begin.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReconciliationView;
